import { Component } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { LoaderService } from 'src/app/Loader/loader.service';
import { BreadcrumbService } from 'src/app/shared/Breadcrumb/breadcrumb.service';
import { breadcrumnsCodes } from 'src/app/shared/constants/global.constants';
import { AuditLogService } from 'src/app/shared/ModelComparison/audit.log.service';

@Component({
  selector: 'app-audit-list',
  templateUrl: './audit-list.component.html',
  styleUrls: ['./audit-list.component.css']
})
export class AuditListComponent {
  _enumBreadcrumb: any;
  auditList: any[] = [];
  totalRecordCount: number = 0;
  rows: number = 10;

  startDate: Date | null = null;
  endDate: Date | null = null;

  // Column filters. The table is server-paginated, so these narrow the page that is
  // currently loaded; the date range above is what filters the full set on the server.
  gridFilters = {
    recordId: '',
    operationType: '',
    entityIdentifier: '',
    fromService: '',
    operatorUsername: '',
    operationDate: ''
  };
  filteredAuditList: any[] = [];

  fieldModificationsByRecord: { [key: string]: any[] | null } = {};
  loadingDetailByRecord: { [key: string]: boolean } = {};

  constructor(
    private breadcrumbService: BreadcrumbService,
    private loaderService: LoaderService,
    private auditLogService: AuditLogService,
    private datePipe: DatePipe,
  ) { }

  ngOnInit(): void {
    this._enumBreadcrumb = breadcrumnsCodes;
    this.breadcrumbService.generateBreadcrumb(this._enumBreadcrumb.AUL);
  }

  getBreadcrumbs(): MenuItem[] {
    return this.breadcrumbService.getBreadcrumbs();
  }

  // Triggered by p-table's lazy loading (initial load and every page/size change)
  loadAuditList(event: any): void {
    const rows = event?.rows ?? this.rows;
    const first = event?.first ?? 0;
    const pageNum = Math.floor(first / rows) + 1;
    this.rows = rows;

    this.loaderService.showLoader();
    // The list endpoint only accepts date-level granularity (proven via Postman as "dd-MMM-yyyy");
    // the pickers show a time component for a nicer UX, but the time portion isn't sent.
    const startDateStr = this.startDate ? this.datePipe.transform(this.startDate, 'dd-MMM-yyyy') ?? undefined : undefined;
    const endDateStr = this.endDate ? this.datePipe.transform(this.endDate, 'dd-MMM-yyyy') ?? undefined : undefined;

    this.auditLogService.GetAuditLogList(pageNum, rows, startDateStr, endDateStr).subscribe({
      next: (res: any) => {
        // Confirmed response shape: { code, message, data: { pageSize, pageNum, total, data: [...] } }
        this.auditList = res?.data?.data ?? [];
        this.totalRecordCount = parseInt(res?.data?.total, 10) || this.auditList.length;
        this.applyGridFilters();
        this.loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Failed to load audit records', error);
        this.auditList = [];
        this.filteredAuditList = [];
        this.totalRecordCount = 0;
        this.loaderService.hideLoader();
      }
    });
  }

  onSearch(): void {
    this.loadAuditList({ first: 0, rows: this.rows });
  }

  onClearFilters(): void {
    this.startDate = null;
    this.endDate = null;
    this.clearGridFilters();
    this.loadAuditList({ first: 0, rows: this.rows });
  }

  applyGridFilters(): void {
    const matches = (value: any, filter: string) =>
      !filter.trim() || String(value ?? '').toLowerCase().includes(filter.trim().toLowerCase());

    this.filteredAuditList = this.auditList.filter(record =>
      matches(record.recordId, this.gridFilters.recordId) &&
      matches(record.operationType, this.gridFilters.operationType) &&
      matches(record.entityIdentifier, this.gridFilters.entityIdentifier) &&
      matches(record.fromService, this.gridFilters.fromService) &&
      matches(record.operatorUsername, this.gridFilters.operatorUsername) &&
      matches(record.operationDate, this.gridFilters.operationDate)
    );
  }

  clearGridFilters(): void {
    this.gridFilters = {
      recordId: '',
      operationType: '',
      entityIdentifier: '',
      fromService: '',
      operatorUsername: '',
      operationDate: ''
    };
    this.applyGridFilters();
  }

  get isGridFiltered(): boolean {
    return Object.values(this.gridFilters).some(value => !!value.trim());
  }

  refreshClick(): void {
    this.loadAuditList({ first: 0, rows: this.rows });
  }

  // Field-level before/after diff is not included in the list response; fetch it
  // lazily per row the first time it's expanded, and cache it after that.
  onRowExpand(record: any): void {
    const id = record?.id;
    if (id === undefined || id === null || this.fieldModificationsByRecord[id] !== undefined) {
      return;
    }
    this.loadingDetailByRecord[id] = true;
    this.auditLogService.GetAuditDetailById(id).subscribe({
      next: (res: any) => {
        this.fieldModificationsByRecord[id] =
          res?.data?.fieldModifications ?? res?.data?.data?.fieldModifications ?? res?.fieldModifications ?? [];
        this.loadingDetailByRecord[id] = false;
      },
      error: (error) => {
        console.error('Failed to load audit record detail for id ' + id, error);
        this.fieldModificationsByRecord[id] = [];
        this.loadingDetailByRecord[id] = false;
      }
    });
  }
}
